import { useState, useCallback, useEffect } from 'react';
import { getGroupbyId, OrganisationDetails, updateOrgDetails, updateOrgLogo } from '../../services';
import { useS3Uploader } from './../useS3Uploader';
import { useToaster, useUserContext } from '../../context';
import { useTranslation } from 'react-i18next';
import imageCompression from 'browser-image-compression';
import { useOrganisationData } from './useOrganisationData';

export const useUpdateOrganisation = (practiseAdminId: string, organisationId: string) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const userAccessToken = localStorage.getItem('user_access_token') || '';
  const { getModifiedFileName, uploadOrganisationLogoFromUri, generateSignedUrl } = useS3Uploader();
  const { showToaster } = useToaster();

  const [fileName, setFileName] = useState<string | null>(null);

  const {
    data: organisationFromStore,
    refetch,
    isLoading
  } = useOrganisationData({
    practiseAdminId,
    organisationId
  });

  const [details, setDetails] = useState<OrganisationDetails | null>(null);

  const goBack = useCallback(() => window.history.back(), []);

  // Fetch organisation details + signed URL
  useEffect(() => {
    const fetchOrganisationDetails = async () => {
      if (!organisationFromStore?.organisationId) return;
      try {
        const practiseList = await getGroupbyId(
          organisationFromStore.organisationId,
          userAccessToken
        );

        const organisationName = practiseList?.name || '';
        const logoUrl = await generateSignedUrl(organisationFromStore.logoUrl);

        setDetails({
          ...organisationFromStore,
          organisationName,
          logoUrl
        });

        // Refetch in background if needed
        refetch();
      } catch (error) {
        console.error('Error fetching organisation details:', error);
      }
    };

    fetchOrganisationDetails();
  }, [
    organisationFromStore?.organisationId,
    organisationFromStore?.logoUrl,
    userAccessToken,
    generateSignedUrl
  ]);

  // Handle logo file upload
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileFromInput = event.target.files?.[0];
      if (!fileFromInput || !user?.userId) return false;
      let file: File = fileFromInput;

      // Allowed formats
      const allowedExtensions = ['png', 'jpg', 'jpeg'];
      const allowedMimeTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

      if (!allowedExtensions.includes(fileExtension) || !allowedMimeTypes.includes(file.type)) {
        showToaster('info', t('files.format'));
        return false;
      }

      // Check size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showToaster('info', t('files.size'));
        return false;
      }

      // Check image dimensions
      const checkDimensions = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > 400 || img.height > 400) {
            showToaster('info', t('files.dimensions'));
            resolve(false);
          } else {
            resolve(true);
          }
        };
        img.onerror = () => resolve(false);
        img.src = URL.createObjectURL(file);
      });

      if (!checkDimensions) return false;

      // Compress image
      try {
        const fileSizeMB = file.size / (1024 * 1024);
        const options: { maxSizeMB: number; maxWidthOrHeight: number; useWebWorker: boolean } = {
          maxWidthOrHeight: 1400,
          useWebWorker: true,
          maxSizeMB: 0
        };

        if (fileSizeMB < 0.5) options.maxSizeMB = fileSizeMB;
        else if (fileSizeMB < 1.5) options.maxSizeMB = fileSizeMB * 0.7;
        else options.maxSizeMB = fileSizeMB * 0.5;

        const compressed = await imageCompression(file, options);
        file = new File([compressed], file.name, { type: compressed.type });
      } catch (err) {
        console.error('Compression error:', err);
        return false;
      }

      try {
        // Upload file
        const modifiedName = getModifiedFileName(file.name, user.userId);
        const key = await uploadOrganisationLogoFromUri(file, modifiedName);
        if (!key) throw new Error('File upload failed.');

        // Update backend
        const payload = { logoUrl: key, organisationId };
        const result = await updateOrgLogo(payload);
        if (!result) throw new Error('Logo update failed.');

        // Update local state immediately with cache buster
        const signedUrl = (await generateSignedUrl(key)) + `?v=${Date.now()}`;
        setDetails((prev) => (prev ? { ...prev, logoUrl: signedUrl } : null));

        // Optional background refetch
        refetch();

        return true;
      } catch (error) {
        console.error('Error uploading or saving logo:', error);
        return false;
      }
    },
    [
      user?.userId,
      getModifiedFileName,
      organisationId,
      practiseAdminId,
      uploadOrganisationLogoFromUri,
      generateSignedUrl,
      t
    ]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: OrganisationDetails) => {
      try {
        const res = await updateOrgDetails(formData);
        if (!res) throw new Error('Update failed.');
        showToaster('success', t('organization.messages.success'));
        return true;
      } catch (error) {
        showToaster('error', t('organization.messages.error'));
        console.error('Error updating organisation details:', error);
        return false;
      }
    },
    [t, showToaster]
  );

  return {
    goBack,
    details,
    handleFileChange,
    handleSubmit,
    setFileName,
    fileName,
    loading: isLoading
  };
};
